<template>
    <sidebar @close="$emit('close')" v-if="firstRun">
        <template v-slot:title>
            {{ collectionRuns ? firstRun.paygroup.collection.name : run.paygroup.name }}
        </template>

        <template v-slot:header>
            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabEarningContentMain" id="tabEarningLinkMain" data-toggle="tab" role="tab" aria-controls="tabEarningContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabEarningContentMain" role="tabpanel" aria-labelledby="tabEarningLinkMain">

                    <info-widget :collapsible="false">
                        <template v-slot:title>Pay Group {{ collectionRuns ? 'Collection' : '' }} Information</template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="row mb-0">
                                    <div class="col">
                                        <div v-if="collectionRuns" class="mui-textfield">
                                            <span class="textfield">
                                                <template v-for="cc in collectionCompanies">
                                                    <span>{{ cc.name }}</span><br/>
                                                </template>
                                            </span>
                                            <label>Companies</label>
                                        </div>
                                        <div v-else class="mui-textfield">
                                            <span class="textfield">{{ runCompany.name }}</span>
                                            <label>Company</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ $filters.datetime(firstRun.process_by_time) }}</span>
                                            <label>Process By</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ $filters.date(firstRun.check_date) }}</span>
                                            <label>Check Date</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ $filters.date(firstRun.pay_period_start_date) + ' - ' + $filters.date(firstRun.pay_period_end_date)}}</span>
                                            <label>Pay Period</label>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <info-widget v-for="run in runs" :key="run.id" :collapsible="false">
                        <!-- TODO KB PG reconsider formatting of status here -->
                        <template v-slot:title><span v-if="runs.length > 1" class="text-small">{{ run.paygroup.name }} / {{ getCompany(run.company_id)?.name }}<br/></span> Status: {{ getRunStatusPretty(run) }}</template>

                        <template v-slot:headerbutton v-if="canCancel(run)">
                            <button class="btn btn-outline-primary" @click.prevent.stop="cancelingRun = run">Cancel Run</button>
                        </template>

                        <slot>
                            <div v-if="run.location_statuses.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Location</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="l in run.location_statuses" :key="l.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ l.name }}</span>
                                            </td>
                                            <td>
                                                <span>{{ getStatusPretty(l.status) }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This pay group is not associated with any locations.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>

            <modal v-if="cancelingRun" :autoshow="true" :is-closeable="true" @close="cancelingRun = null">
                <template v-slot:title>Cancel Run</template>

                <slot>
                    <p>Cancel the {{ cancelingRun.paygroup.name }} run for {{ $filters.date(cancelingRun.pay_period_start_date) + ' - ' + $filters.date(cancelingRun.pay_period_end_date) }}?</p>
                    <p class="mb-0">None of its paychecks will be paid, and it moves to Canceled. A new run can then be created for the same pay period.</p>
                </slot>

                <template v-slot:footer>
                    <button-row stacked>
                        <button type="button" class="btn btn-outline-primary" @click.prevent="cancelingRun = null">Keep Run</button>
                        <button type="button" class="btn btn-primary" @click.prevent="cancelRun(cancelingRun)">Cancel Run</button>
                    </button-row>
                </template>
            </modal>
        </slot>

    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import Modal from '@/components/Modal'

export default {
    props: ['client', 'runId', 'collectionId', 'collectionStartDate'],
    emits: ['close', 'updated'],
    components: {Sidebar, InfoWidget, ButtonRow, Modal, },
    computed: {
        runCompany() {
            if (this.run) {
                return this.getCompany(this.run.company_id)
            }
            return null
        },
        collectionCompanyIds() {
            if (this.collectionRuns) {
                return [...new Set(this.collectionRuns.map(r => r.company_id))]
            }
            return []
        },
        collectionCompanies() {
            if (this.collectionRuns) {
                return this.client.companies.filter(c => this.collectionCompanyIds.includes(c.id))
            }
            return []
        },
        firstRun() {
            return this.run || (this.collectionRuns?.length ? this.collectionRuns[0] : null)
        },
        runs() {
            return this.collectionRuns || (this.run ? [this.run] : [])
        }
    },
    data() {
        return {
            run: null,
            collectionRuns: null,
            activeTab: this.tab || 'main',
            cancelingRun: null,
        }
    },
    watch: {
        runId() {
            this.getData()
        },
        collectionId() {
            this.getData()
        },
        collectionStartDate() {
            this.getData()
        }
    },
    mounted() {
        this.getData()
    },
    methods: {
        getData() {
            this.$store.dispatch('START_LOADING')
            let url
            if (this.collectionId) {
                url = `/clients/${this.client.id}/payroll/collections/${this.collectionId}/runs?pay_period_start_date=${this.collectionStartDate}`
            } else {
                url = `/clients/${this.client.id}/payroll/runs/${this.runId}`
            }
            this.$api.get(url).then(resp => {
                if (this.collectionId) {
                    this.run = null
                    this.collectionRuns = resp
                } else {
                    this.run = resp
                    this.collectionRuns = null
                }
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        getCompany(companyId) {
            return this.client.companies.find(c => c.id == companyId)
        },
        canCancel(run) {
            // Matches PaygroupRun.cancel: an open run with no location approved
            return ['not-started', 'in-progress', 'ready-for-approval'].includes(run.status)
                && !run.approved_location_ids?.length
                && this.$permissions.hasRolesInCompany(this.client.id, run.company_id, this.$store.state.security_roles.CANCEL_PAYROLL_RUN_ROLES)
        },
        cancelRun(run) {
            this.$store.dispatch('START_LOADING')
            this.$api.post(`/clients/${this.client.id}/payroll/runs/${run.id}/cancel`, {}).then(() => {
                this.$store.dispatch('STOP_LOADING')
                this.cancelingRun = null
                this.$emit('updated')
                // A canceled run drops out of its collection's runs, so this sidebar
                // couldn't show it
                if (this.collectionId) {
                    this.$emit('close')
                } else {
                    this.getData()
                }
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        getRunStatusPretty(run) {
            return this.getStatusPretty(this.getRunStatus(run))
        },
        getRunStatus(run) {
            // adjusts displayed overall run status depending on which locations within run user has access to
            // TODO KB PG shortcut this by first checking if user has access to run's whole company and if so just take run.status?
            // TODO KB PG handle 'Waiting for Completion' pseudo-status
            if (['not-started', 'in-progress'].includes(run.status)) {
                let status = 'not-started'
                if (run.location_statuses.find(l => l.status == 'in-progress')) {
                    status = 'in-progress'
                }
                return status
            } else {
                return run.status
            }
        },
        getStatusPretty(status) {
            // 'completed' displays as Approved: approval is the user-facing event, and a run
            // moves approved -> completed on its own as soon as processing finishes
            return {
                'not-started': 'Not Started',
                'in-progress': 'In Progress',
                'ready-for-approval': 'Ready for Approval',
                'approved': 'Approved',
                'completed': 'Approved',
                'canceled': 'Canceled'
            }[status]
        }
    },
}
</script>
